/**
 * Comparison pins (B5/F07) — the bespoke source-connection path.
 *
 * Everything else about a pinned comparison rides the generic workspace-pin
 * machinery (`pinCaptureRegistry` → `workspacePinCreate` → `pinnedObjectsStore`
 * → `openWorkspacePin`). ONE thing cannot: which connection the pin is filed
 * under.
 *
 * The generic path scopes a pin to `tab.connectionInfo.connectionId`, and
 * compare tabs populate that inconsistently — `openDataCompareWithEndpoints`
 * derives it from the TARGET, and `DataCompareSetupDialog`'s own `addTab` call
 * omits it entirely. A comparison spans two connections; filing it under the
 * target (or nowhere) is wrong, so the scope is derived HERE, from
 * `content.source`, and `pinScopeForTab` is the single seam the two generic
 * hooks call instead of reading `connectionInfo` directly.
 *
 * The payload is the session-serialized tab content — the same fields
 * `sessionStore.serializeTab` persists, built by an explicit pick so no
 * run-scoped field (results, `operationId`, row selection) can leak into a
 * durable pin. `connectionMetaInfo.ts` re-enforces that with an allowlist.
 */

import type { Tab } from '../../core/types/workspaceTypes';
import type {
  DataCompareTabContent,
  DataCompareEndpoint,
  SchemaCompareTabContent,
  SchemaCompareEndpoint,
} from '../../core/types/workspaceTypes';
import { isConnectionEndpoint, isFolderEndpoint } from '../../core/types/workspaceTypes';
import type { DataCompareOptions } from '../../core/types/workspaceTypes';
import type { CapturedDefinition } from '../../../database-objects/services/pinnedObjects/pinDivergence';
// W5.1c — ONE options-serialization contract for schema compare, shared with
// the shareable options FILE (W5.1b). Imported rather than re-declared: adding
// a field to `SchemaCompareOptions` without adding it there is a compile error,
// so the file and the pin can never drift apart.
import { pickKnownCompareOptionFields } from '../../schema-compare/utils/compareOptionsFile';

/** Which connection + database a comparison pin is filed under. */
export type ComparePinScope = { connectionId: string; database: string };

/** True for the two compare workspace types. */
export function isComparePinTab(tab: Tab | undefined): boolean {
  return tab?.type === 'data-compare' || tab?.type === 'schema-compare';
}

/**
 * The SOURCE connection + database of a compare tab, or `null` when the source
 * can't anchor a pin — a blueprint-folder source (no connection at all) or an
 * endpoint the user hasn't finished configuring.
 */
export function comparePinScope(tab: Tab | undefined): ComparePinScope | null {
  if (!tab) return null;
  if (tab.type === 'data-compare') {
    const source = (tab.content as DataCompareTabContent).source;
    if (!isConnectionEndpoint(source)) return null;
    if (!source.connectionId || !source.database) return null;
    return { connectionId: source.connectionId, database: source.database };
  }
  if (tab.type === 'schema-compare') {
    const source = (tab.content as SchemaCompareTabContent).source;
    if (!source?.connectionId || !source.database) return null;
    return { connectionId: source.connectionId, database: source.database };
  }
  return null;
}

/**
 * WHY a compare tab's source can't anchor a pin, or `null` when it can
 * (W5.1c fix round 1). `comparePinScope` collapses three different situations
 * into one `null`; the disabled pin control needs to tell them apart, because
 * it now sits permanently on the Schema Compare header instead of hiding in a
 * context menu, and a permanently-visible control that explains itself wrongly
 * is its own defect.
 *
 *  - `'folder'` — the source is a Database Blueprint folder. There is no
 *    connection to file the pin under, so this is structural, not a
 *    one-more-click state. (Pre-existing and out of scope to change: pins are
 *    keyed by connection in `connectionMetaInfo`.)
 *  - `'unconfigured-schema'` / `'unconfigured-data'` — the source just isn't
 *    picked yet. Split by kind ONLY so the wording can be true: a Schema
 *    Compare source is a database, a Data Compare source is a database AND a
 *    table. The old single string named a table on both.
 */
export type ComparePinSourceBlock = 'folder' | 'unconfigured-schema' | 'unconfigured-data';

export function comparePinSourceBlock(tab: Tab | undefined): ComparePinSourceBlock | null {
  if (!tab || !isComparePinTab(tab)) return null;
  if (comparePinScope(tab)) return null; // source is fine

  if (tab.type === 'data-compare') {
    const source = (tab.content as DataCompareTabContent).source;
    // A non-connection endpoint is the blueprint-folder form.
    return source && !isConnectionEndpoint(source) ? 'folder' : 'unconfigured-data';
  }
  const source = (tab.content as SchemaCompareTabContent).source;
  return isFolderEndpoint(source) ? 'folder' : 'unconfigured-schema';
}

/**
 * The connection + database a pin created from `tab` belongs to. Compare tabs
 * answer from their source endpoint; every other tab answers from
 * `connectionInfo`, exactly as before.
 */
export function pinScopeForTab(
  tab: Tab | undefined,
): { connectionId: string | undefined; database: string | undefined } {
  if (isComparePinTab(tab)) {
    const scope = comparePinScope(tab);
    return { connectionId: scope?.connectionId, database: scope?.database };
  }
  return {
    connectionId: tab?.connectionInfo?.connectionId,
    database: tab?.connectionInfo?.database,
  };
}

/**
 * Field-by-field whitelist for a pinned DATA-compare's options (W5.1c fix
 * round 1). The twin of `pickKnownCompareOptionFields` in
 * `compareOptionsFile.ts`, which schema compare shares with its exportable
 * options file; data compare has no such file, so its pick lives here, next to
 * its only consumer, rather than in a module invented to hold it.
 *
 * `satisfies Record<keyof DataCompareOptions, unknown>` is the point: a new
 * field added to `DataCompareOptions` and not listed here fails to compile
 * ("missing property") instead of silently riding along.
 *
 * Why this matters even though `DataCompareOptions` carries no identity fields
 * today: `validateComparePinPayload` (`connectionMetaInfo.ts`) allowlists only
 * the TOP-LEVEL payload keys and never inspects `options`, so anything a future
 * writer hangs on that object reaches disk unexamined. A blind spread is a
 * standing invitation; a whitelist fails closed. This is prospective hardening
 * and one-shape discipline, NOT a claim that a pin is exported — it is not:
 * `connection.json` has no export path (see `pickKnownCompareOptionFields`).
 */
function pickKnownDataCompareOptionFields(options: DataCompareOptions): DataCompareOptions {
  return {
    caseSensitive: options.caseSensitive,
    trimWhitespace: options.trimWhitespace,
    nullEqualsEmpty: options.nullEqualsEmpty,
    numericPrecision: options.numericPrecision,
    dateTimePrecision: options.dateTimePrecision,
    sampleMode: options.sampleMode,
    sampleSize: options.sampleSize,
  } satisfies Record<keyof DataCompareOptions, unknown>;
}

/** A target that is configured enough to be worth restoring. */
function hasUsableTarget(tab: Tab): boolean {
  if (tab.type === 'data-compare') {
    const target = (tab.content as DataCompareTabContent).target;
    if (!target) return false;
    if (isConnectionEndpoint(target)) return !!(target.connectionId && target.database);
    return !!target.folderPath;
  }
  const target = (tab.content as SchemaCompareTabContent).target;
  return !!(target?.connectionId || target?.folderPath);
}

/**
 * The live definition of a compare tab, in the shape the generic pin builders
 * consume. Explicit picks, mirroring `sessionStore.serializeTab`'s compare
 * branches — everything run-scoped (`summary`, `operationId`, `selectedRowKey`,
 * `selectedRowKeys`, `hasCompared`, `needsRefresh`, `error`, `activeDiffTab`,
 * `filteredCounts`, sample flags, `autoCompare`) is deliberately absent.
 *
 * `filters` / `sorting` DO travel: they are the results grid's view
 * configuration, persisted by the session serializer for the same reason, and
 * they carry no row identity.
 *
 * **Empty arrays are omitted, not written.** This keeps the capture symmetric
 * with `deserializeTab`, which is what makes divergence honest across the
 * pin → reopen → capture loop. A live tab has no `filters`/`sorting` at all
 * until the user touches the filter UI, so a pin written from it omits both;
 * `deserializeTab` then ALWAYS materializes them as `[]` on reopen. Including
 * `[]` here would make that reopened tab differ from the pin it came from, so
 * an untouched pinned comparison would read as diverged forever. Omitting
 * empties collapses "absent" and "empty" to the same payload from both
 * directions — which `?? []` would not: that inverts the mismatch for every
 * pin already on disk.
 */
export function captureComparePinDefinition(tab: Tab | undefined): CapturedDefinition | null {
  if (!tab || !isComparePinTab(tab)) return null;
  if (!comparePinScope(tab)) return null;
  if (!hasUsableTarget(tab)) return null;

  if (tab.type === 'data-compare') {
    const c = tab.content as DataCompareTabContent;
    return {
      workspaceType: 'data-compare',
      payload: {
        source: c.source,
        target: c.target,
        // `?.length`, not a truthiness check — see the header note on empties.
        ...(c.keyMappings?.length ? { keyMappings: c.keyMappings } : {}),
        ...(c.valueMappings?.length ? { valueMappings: c.valueMappings } : {}),
        // Fix round 1 — whitelisted, exactly like the schema-compare branch
        // below. This branch was left spreading `c.options` raw, twenty lines
        // from the fix, which is the classic way a hardening pass leaves half
        // the hazard in place.
        ...(c.options ? { options: pickKnownDataCompareOptionFields(c.options) } : {}),
        ...(c.filters?.length ? { filters: c.filters } : {}),
        ...(c.sorting?.length ? { sorting: c.sorting } : {}),
      },
    };
  }

  const c = tab.content as SchemaCompareTabContent;
  return {
    workspaceType: 'schema-compare',
    payload: {
      // Endpoints are passed through WHOLE, with no inner-key filter at all.
      // Be honest about the asymmetry rather than implying a guard that isn't
      // here (fix round 1):
      //   - endpoints: carry identity BY DESIGN (connectionId, database,
      //     folderPath, per-endpoint `schemaFilter`) — a pin that dropped them
      //     could not reopen anything. Nothing filters their inner keys, so
      //     junk hung on `content.source` does ride into the payload. The only
      //     check is structural, at the envelope, in
      //     `validateComparePinPayload`: source must name a connection +
      //     database, target must merely be an object.
      //   - options: whitelisted field by field (below), because unlike the
      //     endpoints there is no reason for anything beyond the known option
      //     fields to be in there.
      // The exportable options FILE (W5.1b) carries no endpoint at all, which
      // is why only the options half is shared with `compareOptionsFile.ts`.
      source: c.source,
      target: c.target,
      // W5.1c — never spread the live `options` object straight in. It is
      // rehydrated unsanitised from persisted stores, and the envelope
      // validator allowlists only TOP-LEVEL payload keys, so it never looks
      // inside `options`. See `pickKnownCompareOptionFields` for why this is
      // one-shape discipline plus defense in depth — and specifically NOT the
      // "pins are exported" claim, which is false.
      ...(c.options ? { options: pickKnownCompareOptionFields(c.options) } : {}),
      ...(c.compareWholeDatabase !== undefined
        ? { compareWholeDatabase: c.compareWholeDatabase }
        : {}),
    },
  };
}

/** `schema.table`, or just `table` on schemaless engines. */
function tableLabel(endpoint: DataCompareEndpoint | undefined): string {
  if (!endpoint) return '?';
  return [endpoint.schema, endpoint.table].filter(Boolean).join('.') || '?';
}

function databaseLabel(endpoint: SchemaCompareEndpoint | undefined): string {
  return endpoint?.database || endpoint?.folderPath || '?';
}

/**
 * Auto-generated pin name. Never empty — `validatePinnedObjectRef` rejects a
 * nameless pin, and the user renames it from the tree anyway.
 */
export function autoComparePinName(captured: CapturedDefinition): string {
  if (captured.workspaceType === 'data-compare') {
    const p = captured.payload;
    return `${tableLabel(p.source as DataCompareEndpoint)} → ${tableLabel(p.target as DataCompareEndpoint)}`;
  }
  const p = captured.payload as { source?: SchemaCompareEndpoint; target?: SchemaCompareEndpoint; compareWholeDatabase?: boolean };
  const databases = `${databaseLabel(p.source)} → ${databaseLabel(p.target)}`;
  const object = [p.source?.objectSchema, p.source?.objectName].filter(Boolean).join('.');
  return p.compareWholeDatabase === false && object ? `${object} (${databases})` : databases;
}
